import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import uuid from 'uuid';
import calculatePortalElementPosition, { PortalElementAlignment } from './utilities/calculatePortalElementPosition';
import Button from './Button';
import Icon from './Icon';

export type TooltipProps = {
    className?: string;
    children: ReactNode;
    interaction?: 'manual' | 'automatic';
    preferredAlignment?: PortalElementAlignment;
    triggerContent: ReactNode;
};

const waitForFramePaint = () =>
    new Promise((resolve) => {
        requestAnimationFrame(resolve);
    });

const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
    ({ children, className, interaction = 'automatic', preferredAlignment = { vertical: 'bottom', horizontal: 'center' }, triggerContent }, ref) => {
        const isManual = interaction === 'manual';
        const [visible, setVisible] = useState(false);
        const tooltipRef = useRef<HTMLDivElement>(ref && 'current' in ref ? ref.current : null);
        const triggerWrapperRef = useRef<HTMLSpanElement & HTMLButtonElement>(null);
        const closeButtonRef = useRef<HTMLButtonElement>(null);
        const tooltipIdRef = useRef(uuid());

        useEffect(() => {
            if (!tooltipRef.current || !triggerWrapperRef.current) return;

            const tooltipElement = tooltipRef.current;
            const triggerElement = triggerWrapperRef.current;

            if (visible) {
                waitForFramePaint()
                    .then(() => {
                        const { left, top, arrowShiftX, alignment } = calculatePortalElementPosition(
                            triggerElement,
                            tooltipElement,
                            preferredAlignment
                        );

                        tooltipElement.classList.add(alignment.vertical);
                        tooltipElement.style.setProperty('--arrow-shift-x', `${arrowShiftX}px`);
                        tooltipElement.style.setProperty('--top', `${top}px`);
                        tooltipElement.style.setProperty('--left', `${left}px`);

                        return waitForFramePaint();
                    })
                    .then(() => closeButtonRef.current?.focus());
            } else {
                tooltipElement.classList.remove("top", "bottom");
                tooltipElement.style.removeProperty('--arrow-shift-x');
                tooltipElement.style.removeProperty('--top');
                tooltipElement.style.removeProperty('--left');
            }
        }, [preferredAlignment, visible, triggerContent, children]);

        useEffect(() => {
            if (!isManual || !visible) return;

            const handleOutsidePointerDown = (event: PointerEvent) => {
                const target = event.target as Node;

                if (!triggerWrapperRef.current.contains(target) && !tooltipRef.current.contains(target)) {
                    setVisible(false);
                }
            };

            const handleScroll = () => setVisible(false);

            document.addEventListener('pointerdown', handleOutsidePointerDown);
            document.addEventListener('scroll', handleScroll);

            return () => {
                document.removeEventListener('pointerdown', handleOutsidePointerDown);
                document.removeEventListener('scroll', handleScroll);
            };
        }, [isManual, visible]);

        const handleCloseButtonClick = () => {
            setVisible(false);

            triggerWrapperRef.current.focus();
        };

        return (
            <>
                {isManual ? (
                    <button
                        className="tooltip-trigger btn-restyled"
                        aria-describedby={tooltipIdRef.current}
                        aria-expanded={visible}
                        ref={triggerWrapperRef}
                        onClick={() => setVisible((prevVisible) => !prevVisible)}
                        type="button"
                    >
                        {triggerContent}
                    </button>
                ) : (
                    <span
                        className="tooltip-trigger"
                        aria-describedby={tooltipIdRef.current}
                        ref={triggerWrapperRef}
                        onMouseEnter={() => setVisible(true)}
                        onFocus={() => setVisible(true)}
                        onMouseLeave={() => setVisible(false)}
                        onBlur={() => setVisible(false)}
                        tabIndex={0}
                        role="button"
                    >
                        {triggerContent}
                    </span>
                )}
                {visible &&
                    createPortal(
                        <span
                            className={classNames("fw-tooltip", className, { manual: isManual })}
                            id={tooltipIdRef.current}
                            ref={tooltipRef}
                            role="tooltip"
                        >
                            {isManual && (
                                <Button
                                    className="tooltip-close-button"
                                    displayStyle="unstyled"
                                    aria-label="Close tooltip"
                                    icons={{ center: <Icon symbol="close" /> }}
                                    onClick={handleCloseButtonClick}
                                    ref={closeButtonRef}
                                />
                            )}
                            {children}
                        </span>,
                        document.body
                    )}
            </>
        );
    }
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
